import React, { useState } from "react";
import api, { API_BASE } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import Terminal from "@/components/Terminal";
import { Zap, Crosshair, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const ReconEngine = () => {
  const [target, setTarget] = useState("scanme.cyberspherex.io");
  const [mode, setMode] = useState("recon");
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [findings, setFindings] = useState([]);

  const start = async () => {
    setBusy(true);
    setFindings([]);
    setLines([{ type: "cmd", text: `${mode} --aggressive --target ${target}` }]);
    try {
      const { data: scan } = await api.post("/recon/scan", { target, mode });
      const url = `${API_BASE}/recon/terminal/${scan.id}`;
      const es = new EventSource(url, { withCredentials: true });
      es.onmessage = (ev) => {
        try {
          const j = JSON.parse(ev.data);
          if (j.line) setLines((p) => [...p, { type: "out", text: j.line }]);
          if (j.done) {
            es.close();
            // pull findings
            api.get(`/recon/scan/${scan.id}`).then(({ data }) => setFindings(data.findings || []));
            setBusy(false);
            toast.success("Recon complete");
          }
        } catch (err) {
          console.error("[recon] SSE parse error:", err);
        }
      };
      es.onerror = () => { es.close(); setBusy(false); };
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      setBusy(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Attack Surface / Recon"
        title="Recon Engine"
        subtitle="Active + passive reconnaissance across network, web, API and cloud surfaces."
        actions={
          <TButton data-testid="start-scan" icon={Zap} onClick={start} disabled={busy}>
            {busy ? "Scanning..." : "Start Recon"}
          </TButton>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="csx-card p-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Target (Host / CIDR / URL)</span>
              <div className="mt-1.5 flex items-center gap-2 border border-slate-800 bg-slate-950 px-3 py-2.5 focus-within:border-cyan-500/70">
                <Crosshair className="h-4 w-4 text-cyan-400" />
                <input
                  data-testid="recon-target"
                  className="flex-1 bg-transparent outline-none text-sm font-mono text-slate-100"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                />
              </div>
            </label>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {["recon", "enum", "exploit", "pivot"].map((m) => (
                <button
                  key={m}
                  data-testid={`mode-${m}`}
                  onClick={() => setMode(m)}
                  className={[
                    "text-[11px] uppercase tracking-widest py-2 border transition-all",
                    mode === m ? "border-cyan-400 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400 hover:border-slate-700"
                  ].join(" ")}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <Terminal lines={lines} height="h-96" />
        </div>

        <div className="csx-card p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Findings</div>
          {findings.length === 0 ? (
            <div className="text-xs text-slate-500">No findings yet. Run a scan to populate.</div>
          ) : (
            <ul className="space-y-2 text-sm">
              {findings.map((f) => {
                const col =
                  f.risk === "critical" ? "text-rose-300 border-rose-500/40"
                  : f.risk === "high" ? "text-amber-300 border-amber-500/40"
                  : f.risk === "medium" ? "text-cyan-300 border-cyan-500/40"
                  : "text-slate-300 border-slate-700";
                return (
                  <li key={`${f.port}-${f.service}`} className={`border ${col} p-2.5 bg-slate-950/40`}>
                    <div className="flex items-center justify-between">
                      <span className="font-bold">:{f.port} {f.service}</span>
                      <span className="text-[10px] uppercase tracking-widest">{f.risk}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{f.version}</div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="mt-4 flex items-center gap-2 text-[10px] text-slate-500">
            <AlertTriangle className="h-3 w-3" /> Sandbox-simulated. No live traffic generated.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReconEngine;
